import { useEffect, useRef } from "react";
import styles from "./RichTextEditor.module.css";

/**
 * Editor de texto enriquecido mínimo (sin dependencias):
 * negrita, cursiva y alineación. Trabaja con HTML.
 *
 * Props:
 *  - value: HTML actual (string)
 *  - onChange(html): callback al editar
 *  - placeholder: texto guía cuando está vacío
 */
export default function RichTextEditor({ value, onChange, placeholder }) {
  const ref = useRef(null);

  // sincroniza el contenido externo (p. ej. al cambiar de servicio) sin pisar
  // lo que el usuario está escribiendo
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (document.activeElement === el) return;
    if (el.innerHTML !== (value || "")) el.innerHTML = value || "";
  }, [value]);

  function emit() {
    onChange?.(ref.current?.innerHTML || "");
  }

  function exec(cmd, arg = null) {
    document.execCommand(cmd, false, arg);
    ref.current?.focus();
    emit();
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.toolbar}>
        <button type="button" className={styles.tool} title="Negrita"
          onMouseDown={(e) => e.preventDefault()} onClick={() => exec("bold")}>
          <b>B</b>
        </button>
        <button type="button" className={styles.tool} title="Cursiva"
          onMouseDown={(e) => e.preventDefault()} onClick={() => exec("italic")}>
          <i>I</i>
        </button>

        <span className={styles.sep} />

        <button type="button" className={styles.tool} title="Alinear a la izquierda"
          onMouseDown={(e) => e.preventDefault()} onClick={() => exec("justifyLeft")}>
          <AlignIcon variant="left" />
        </button>
        <button type="button" className={styles.tool} title="Centrar"
          onMouseDown={(e) => e.preventDefault()} onClick={() => exec("justifyCenter")}>
          <AlignIcon variant="center" />
        </button>
        <button type="button" className={styles.tool} title="Alinear a la derecha"
          onMouseDown={(e) => e.preventDefault()} onClick={() => exec("justifyRight")}>
          <AlignIcon variant="right" />
        </button>
        <button type="button" className={styles.tool} title="Justificar"
          onMouseDown={(e) => e.preventDefault()} onClick={() => exec("justifyFull")}>
          <AlignIcon variant="full" />
        </button>

        <span className={styles.sep} />

        <button type="button" className={styles.tool} title="Viñetas"
          onMouseDown={(e) => e.preventDefault()} onClick={() => exec("insertUnorderedList")}>
          <ListIcon />
        </button>

        <select
          className={styles.size}
          title="Tamaño del texto"
          defaultValue=""
          onMouseDown={(e) => e.stopPropagation()}
          onChange={(e) => {
            if (e.target.value) exec("fontSize", e.target.value);
            e.target.value = "";
          }}
        >
          <option value="" disabled>Aa</option>
          <option value="2">Pequeño</option>
          <option value="3">Normal</option>
          <option value="5">Grande</option>
          <option value="6">Más grande</option>
        </select>
      </div>

      <div
        ref={ref}
        className={styles.editor}
        contentEditable
        role="textbox"
        aria-multiline="true"
        data-placeholder={placeholder}
        onInput={emit}
        suppressContentEditableWarning
      />
    </div>
  );
}

function ListIcon() {
  return (
    <svg width="16" height="14" viewBox="0 0 16 14" fill="none" aria-hidden="true">
      <circle cx="3" cy="3.5" r="1.2" fill="currentColor" />
      <circle cx="3" cy="10.5" r="1.2" fill="currentColor" />
      <path d="M7 3.5 H14 M7 10.5 H14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function AlignIcon({ variant }) {
  const lines = {
    left:   ["M2 3 H14", "M2 7 H10", "M2 11 H13"],
    center: ["M2 3 H14", "M4 7 H12", "M3 11 H13"],
    right:  ["M2 3 H14", "M6 7 H14", "M3 11 H14"],
    full:   ["M2 3 H14", "M2 7 H14", "M2 11 H14"],
  }[variant];
  return (
    <svg width="16" height="14" viewBox="0 0 16 14" fill="none" aria-hidden="true">
      {lines.map((d, i) => (
        <path key={i} d={d} stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      ))}
    </svg>
  );
}
